/* eslint-disable complexity */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {
    Collapsible,
    CollapsibleButton,
    CollapsibleContent,
    CollapsibleHeader,
} from 'components/Collapsible';
import { Icon } from 'components/Icon';
import { Pill } from 'components/Misc/Pill';
import { Inline } from 'components/Typography';
import { VisuallyHidden } from 'components/Utilities';
import { type ToggleableProps, toggleablePropTypes } from 'utilsTS/hooks';
import { ICON } from 'utilsTS/icon';
import { isElementOfComponent, omitProps, pickProps } from 'utilsTS/react';
import { SecondaryNavigationItem, SecondaryNavigationItemProps } from './SecondaryNavigationItem';

export interface SecondaryNavigationHeadingItemProps
    extends SecondaryNavigationItemProps,
        ToggleableProps {
    label?: string;
    icon?: ICON;
    iconActive?: ICON;
    pill?: React.ReactNode | number;
    badge?: React.ReactNode;
    showIcon?: boolean;
    collapsible?: boolean;
    subItemSelected?: boolean;
    isOpen?: boolean;
    isExpanded?: boolean;
    className?: string;
}

export const SecondaryNavigationHeadingItem: React.FC<SecondaryNavigationHeadingItemProps> = ({
    label,
    icon: defaultIcon,
    iconActive,
    pill,
    badge,
    showIcon,
    collapsible = true,
    subItemSelected = false,
    isOpen = false,
    isExpanded = true,
    selected,
    className,
    children,
    ...rest
}) => {
    const shouldBeHighlighted = selected || (subItemSelected && (!isExpanded || !isOpen));
    const icon = shouldBeHighlighted ? iconActive || defaultIcon : defaultIcon;
    const labelClassName = showIcon && !icon ? 'g-sidenav-offseted-label' : '';
    const buttonProps = ('buttonProps' in rest ? rest.buttonProps : {}) as object;

    const getPill = () => {
        if (!!pill && isElementOfComponent(pill, Pill)) return pill;
        if (typeof pill === 'number' && pill !== 0)
            return <Pill value={pill as number} variant={Pill.VARIANT.UNREAD} />;
        return null;
    };

    const heading = (
        <>
            <span className={cx(collapsible || 'g-sidenav-noninteractive-header')}>
                {icon && showIcon && collapsible && (
                    <Icon
                        icon={icon}
                        color={Icon.COLOR.UI_BLUE}
                        size={Icon.SIZE.SIZE_24}
                        className="g-sidenav-icon"
                    />
                )}

                {isExpanded && <Inline className={cx(labelClassName)}>{label}</Inline>}
                {!isExpanded && <VisuallyHidden>{label}</VisuallyHidden>}
            </span>
            {isExpanded && collapsible && badge}
            {collapsible && (!isOpen || !isExpanded) && getPill()}
        </>
    );

    if (!collapsible || !isExpanded) {
        return (
            <li className={cx('g-sidenav-section', shouldBeHighlighted && 'g-sidenav-selected')}>
                {heading}
                {isExpanded && <ul>{children}</ul>}
            </li>
        );
    }

    return (
        <Collapsible
            component="li"
            className={className}
            isOpen={isOpen}
            {...pickProps(rest, Collapsible)}
        >
            <CollapsibleHeader
                button={
                    <CollapsibleButton
                        {...buttonProps}
                        centered
                        coverParent={false}
                        className={cx(
                            'g-sidenav-heading',
                            shouldBeHighlighted && 'g-sidenav-selected',
                        )}
                    >
                        {heading}
                    </CollapsibleButton>
                }
                {...omitProps(rest, SecondaryNavigationItem, [
                    'onOpen',
                    'onBeforeOpen',
                    'onClose',
                    'onBeforeClose',
                    'buttonProps',
                ])}
            />
            <CollapsibleContent>
                <ul className="g-sidenav-subitems">{children}</ul>
            </CollapsibleContent>
        </Collapsible>
    );
};

SecondaryNavigationHeadingItem.displayName = 'SecondaryNavigationHeadingItem';
SecondaryNavigationHeadingItem.propTypes = {
    ...toggleablePropTypes,
    label: PropTypes.string,
    icon: PropTypes.oneOf(Object.values(Icon.ICON)),
    iconActive: PropTypes.oneOf(Object.values(Icon.ICON)),
    pill: PropTypes.oneOfType([PropTypes.node, PropTypes.number]),
    badge: PropTypes.node,
    showIcon: PropTypes.bool,
    collapsible: PropTypes.bool,
    subItemSelected: PropTypes.bool,
    isOpen: PropTypes.bool,
    isExpanded: PropTypes.bool,
    className: PropTypes.string,
    onToggle: PropTypes.func,
};
